import { Outlet, NavLink, useLocation } from 'react-router-dom'
import OfflineBanner from '@/components/ui/OfflineBanner'
import InstallPrompt from '@/components/ui/InstallPrompt'
import SOSButton from '@/components/sos/SOSButton'
import styles from './MainLayout.module.css'

const navItems = [
  { path: '/', label: 'Home', icon: 'home' },
  { path: '/programs', label: 'Programs', icon: 'programs' },
  { path: '/library', label: 'Library', icon: 'library' },
  { path: '/my-child', label: 'My Child', icon: 'child' },
]

function NavIcon({ name, active }: { name: string; active: boolean }) {
  const color = active ? 'var(--color-sage)' : 'var(--color-charcoal-light)'

  switch (name) {
    case 'home':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      )
    case 'programs':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
          <line x1="9" y1="7" x2="17" y2="7" />
          <line x1="9" y1="11" x2="14" y2="11" />
        </svg>
      )
    case 'library':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
      )
    case 'child':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2">
          <circle cx="12" cy="8" r="5" />
          <path d="M20 21a8 8 0 1 0-16 0" />
        </svg>
      )
    default:
      return null
  }
}

export default function MainLayout() {
  const location = useLocation()

  return (
    <div className={styles.container}>
      <OfflineBanner />
      <header className={styles.header}>
        <div className={styles.logoGroup}>
          <h1 className={styles.logo}>MelloMap</h1>
          <span className={styles.tagline}>Calm child, calmer you</span>
        </div>
        <NavLink to="/settings" className={styles.settingsButton}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </NavLink>
      </header>

      <main className={styles.content}>
        <Outlet />
      </main>

      <InstallPrompt />

      <nav className={styles.tabBar}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={`${styles.tabItem} ${isActive ? styles.active : ''}`}
            >
              <NavIcon name={item.icon} active={isActive} />
              <span className={styles.tabLabel}>{item.label}</span>
            </NavLink>
          )
        })}
      </nav>

      <SOSButton />
    </div>
  )
}
